import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import {
  getDeliveryLogsByTicket,
  createDeliveryLog,
  updateDeliveryLog,
  deleteDeliveryLog,
  uploadDeliveryLogAttachment,
  getDeliveryLogAttachmentUrl,
  deleteDeliveryLogAttachment,
} from '../../../api/deliveryLogs';
import { canEditDeliveryLog, canDeleteDeliveryLog } from '../../../middleware/betaPermissions';
import { useConfirm } from '../../common/ConfirmDialog';
import { useToast } from '../../common/Toast';
import Avatar from '../../common/Avatar';
import './TicketDeliveryLogs.scss';

const LABEL_COLORS = {
  Frontend: '#667eea',
  Backend: '#38a169',
  QA: '#ed8936',
  Design: '#ed64a6',
  PM: '#9f7aea',
};

const PRESET_LABELS = ['Frontend', 'Backend', 'QA', 'Design', 'PM'];
const ENV_OPTIONS = ['staging', 'production', 'dev'];
const ENV_LABELS = { staging: 'Staging', production: 'Production', dev: 'Dev' };
const MAX_FILE_SIZE = 50 * 1024 * 1024;

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd} ${hh}:${min}`;
};

// ─── Main Component (sidebar-compact) ───
const TicketDeliveryLogs = ({ ticketId, ticket }) => {
  const { profile } = useAuth();
  const { confirm } = useConfirm();
  const toast = useToast();
  const { companyUsers } = useBetaData();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalState, setModalState] = useState(null); // null | { mode: 'add' } | { mode: 'edit', log }

  const loadLogs = useCallback(async () => {
    setLoading(true);
    const { data } = await getDeliveryLogsByTicket(ticketId);
    setLogs(data || []);
    setLoading(false);
  }, [ticketId]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const handleCreate = async (formData, files) => {
    const { data, error } = await createDeliveryLog(ticketId, formData);
    if (error) { toast.error('新增交付紀錄失敗'); return false; }

    let logWithAttachments = { ...data };
    if (files.length > 0) {
      const attachments = [];
      for (const file of files) {
        const res = await uploadDeliveryLogAttachment(ticketId, data.id, file);
        if (res.data) attachments.push(res.data);
      }
      logWithAttachments.attachments = attachments;
    }

    setLogs((prev) => [...prev, logWithAttachments]);
    setModalState(null);
    toast.success('已新增交付紀錄');
    return true;
  };

  const handleUpdate = async (logId, formData, newFiles, removedAttIds) => {
    const { data, error } = await updateDeliveryLog(logId, formData);
    if (error) { toast.error('更新失敗'); return false; }

    for (const attId of removedAttIds) {
      const att = logs.find((l) => l.id === logId)?.attachments?.find((a) => a.id === attId);
      if (att) await deleteDeliveryLogAttachment(attId, att.file_path);
    }

    const newAttachments = [];
    for (const file of newFiles) {
      const res = await uploadDeliveryLogAttachment(ticketId, logId, file);
      if (res.data) newAttachments.push(res.data);
    }

    setLogs((prev) =>
      prev.map((l) => {
        if (l.id !== logId) return l;
        const existingAtts = (l.attachments || []).filter((a) => !removedAttIds.includes(a.id));
        return { ...data, attachments: [...existingAtts, ...newAttachments] };
      })
    );
    setModalState(null);
    toast.success('已更新');
    return true;
  };

  const handleDelete = async (logId) => {
    const ok = await confirm({
      title: '刪除交付紀錄',
      message: '確定要刪除這筆交付紀錄嗎？',
      confirmText: '刪除',
      danger: true,
    });
    if (!ok) return;
    const { error } = await deleteDeliveryLog(logId);
    if (error) { toast.error('刪除失敗'); return; }
    setLogs((prev) => prev.filter((l) => l.id !== logId));
    toast.success('已刪除');
  };

  return (
    <div className="ticket-delivery-logs">
      <div className="dl-header">
        <span className="dl-title">
          交付紀錄
          {logs.length > 0 && <span className="dl-count">{logs.length}</span>}
        </span>
        <button className="dl-add-btn" onClick={() => setModalState({ mode: 'add' })}>+</button>
      </div>

      {loading ? (
        <div className="dl-empty">載入中...</div>
      ) : logs.length === 0 ? (
        <div className="dl-empty">尚無紀錄</div>
      ) : (
        <div className="dl-list">
          {logs.map((log, idx) => (
            <CompactLogEntry
              key={log.id}
              log={log}
              profile={profile}
              companyUsers={companyUsers}
              isLast={idx === logs.length - 1}
              onEdit={() => setModalState({ mode: 'edit', log })}
              onDelete={() => handleDelete(log.id)}
            />
          ))}
        </div>
      )}

      {modalState && (
        <DeliveryLogModal
          mode={modalState.mode}
          initialData={modalState.log}
          onSubmit={modalState.mode === 'add'
            ? handleCreate
            : (formData, files, removedIds) => handleUpdate(modalState.log.id, formData, files, removedIds)
          }
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
};

// ─── Timeline Log Entry (for sidebar) ───
const CompactLogEntry = ({ log, profile, companyUsers, onEdit, onDelete, isLast }) => {
  const user = log.user || {};
  const userProfile = companyUsers?.find((u) => u.id === user.id) || user;
  const labelColor = LABEL_COLORS[log.label] || '#718096';
  const canEdit = canEditDeliveryLog(profile, log);
  const canDelete = canDeleteDeliveryLog(profile, log);

  return (
    <div className="dl-entry">
      {/* Timeline dot + line */}
      <div className="dl-timeline">
        <div className="dl-dot" style={{ borderColor: labelColor }} />
        {!isLast && <div className="dl-line" />}
      </div>

      {/* Content */}
      <div className="dl-entry-body">
        {/* Line 1: Label */}
        <div className="dl-entry-label-row">
          <span className="dl-entry-badge" style={{ background: labelColor }}>{log.label}</span>
        </div>
        {/* Line 2: Version + Env */}
        {(log.version || log.environment) && (
          <div className="dl-entry-meta">
            {log.version && <span className="dl-entry-version">{log.version}</span>}
            {log.version && log.environment && <span className="dl-entry-arrow">→</span>}
            {log.environment && <span className={`dl-entry-env ${log.environment}`}>{ENV_LABELS[log.environment]}</span>}
          </div>
        )}
        {log.note && <div className="dl-entry-note">{log.note}</div>}
        {log.attachments?.length > 0 && (
          <CompactAttachments attachments={log.attachments} />
        )}
        {/* Line 3: User + Time + Actions */}
        <div className="dl-entry-footer">
          <span className="dl-entry-time">
            {userProfile.name || userProfile.email}・{formatTime(log.created_at)}
          </span>
          {(canEdit || canDelete) && (
            <span className="dl-entry-actions">
              {canEdit && <button onClick={onEdit} title="編輯">✎</button>}
              {canDelete && <button className="del" onClick={onDelete} title="刪除">✕</button>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Compact Attachments ───
const CompactAttachments = ({ attachments }) => {
  const [urls, setUrls] = useState({});

  useEffect(() => {
    const load = async () => {
      const result = {};
      await Promise.all(
        attachments.map(async (att) => {
          const { data } = await getDeliveryLogAttachmentUrl(att.file_path);
          if (data) result[att.id] = data;
        })
      );
      setUrls(result);
    };
    if (attachments.length > 0) load();
  }, [attachments]);

  return (
    <div className="dl-entry-attachments">
      {attachments.map((att) => {
        const url = urls[att.id];
        const isImg = att.file_type?.startsWith('image/');
        if (isImg && url) {
          return <img key={att.id} className="dl-att-thumb" src={url} alt={att.file_name} onClick={() => window.open(url, '_blank')} />;
        }
        return (
          <span key={att.id} className="dl-att-file" onClick={() => url && window.open(url, '_blank')}>
            📎 {att.file_name}
          </span>
        );
      })}
    </div>
  );
};

// ─── Modal for Add / Edit ───
const DeliveryLogModal = ({ mode, initialData, onSubmit, onClose }) => {
  const isEdit = mode === 'edit';
  const [label, setLabel] = useState(initialData?.label || '');
  const [version, setVersion] = useState(initialData?.version || '');
  const [environment, setEnvironment] = useState(initialData?.environment || '');
  const [note, setNote] = useState(initialData?.note || '');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [removedAttIds, setRemovedAttIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const existingAttachments = isEdit
    ? (initialData.attachments || []).filter((a) => !removedAttIds.includes(a.id))
    : [];

  const handleSubmit = async () => {
    if (!label.trim()) return;
    setSubmitting(true);
    const formData = { label: label.trim(), version: version.trim(), environment, note: note.trim() };
    if (isEdit) {
      await onSubmit(formData, pendingFiles, removedAttIds);
    } else {
      await onSubmit(formData, pendingFiles);
    }
    setSubmitting(false);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.size <= MAX_FILE_SIZE);
    setPendingFiles((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  return ReactDOM.createPortal(
    <div className="dlm-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dlm-modal">
        <div className="dlm-header">
          <h3>{isEdit ? '編輯交付紀錄' : '新增交付紀錄'}</h3>
          <button className="dlm-close" onClick={onClose}>✕</button>
        </div>

        <div className="dlm-body">
          {/* Label */}
          <div className="dlf-row">
            <label className="dlf-label">角色 / 標籤 *</label>
            <div className="dlf-chips">
              {PRESET_LABELS.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`dlf-chip ${label === p ? 'active' : ''}`}
                  style={label === p ? { background: LABEL_COLORS[p] || '#718096' } : {}}
                  onClick={() => setLabel(label === p ? '' : p)}
                >
                  {p}
                </button>
              ))}
            </div>
            <input
              className="dlf-input"
              placeholder="或輸入自訂標籤..."
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              autoFocus
            />
          </div>

          {/* Version + Environment */}
          <div className="dlf-row-inline">
            <div className="dlf-row">
              <label className="dlf-label">版本號（選填）</label>
              <input className="dlf-input" placeholder="例：v0.7.0" value={version} onChange={(e) => setVersion(e.target.value)} />
            </div>
            <div className="dlf-row">
              <label className="dlf-label">環境（選填）</label>
              <div className="dlf-env-chips">
                {ENV_OPTIONS.map((env) => (
                  <button
                    key={env}
                    type="button"
                    className={`dlf-env-chip ${environment === env ? `active ${env}` : ''}`}
                    onClick={() => setEnvironment(environment === env ? '' : env)}
                  >
                    {ENV_LABELS[env]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="dlf-row">
            <label className="dlf-label">備註（選填）</label>
            <textarea className="dlf-textarea" placeholder="輸入備註..." value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
          </div>

          {/* Attachments */}
          <div className="dlf-row">
            <label className="dlf-label">附件（選填）</label>
            <div className="dlf-upload-area">
              <button type="button" className="dlf-upload-btn" onClick={() => fileInputRef.current?.click()}>📎 上傳檔案</button>
              {existingAttachments.map((att) => (
                <span key={att.id} className="dlf-pending-file">
                  {att.file_name}
                  <button className="remove-file" onClick={() => setRemovedAttIds((p) => [...p, att.id])}>×</button>
                </span>
              ))}
              {pendingFiles.map((f, i) => (
                <span key={i} className="dlf-pending-file">
                  {f.name}
                  <button className="remove-file" onClick={() => setPendingFiles((p) => p.filter((_, j) => j !== i))}>×</button>
                </span>
              ))}
            </div>
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
          </div>
        </div>

        <div className="dlm-footer">
          <button className="dlf-submit" onClick={handleSubmit} disabled={!label.trim() || submitting}>
            {submitting ? '提交中...' : isEdit ? '更新' : '提交'}
          </button>
          <button className="dlf-cancel" onClick={onClose}>取消</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TicketDeliveryLogs;
